import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

export function SchoolEmail() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const { toast } = useToast();

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!user) {
        throw new Error("You must be logged in");
      }

      console.log('[FRONTEND] Creating email address in Clerk:', email);

      // Check if email already exists
      const existingEmail = user.emailAddresses.find(
        e => e.emailAddress.toLowerCase() === email.toLowerCase()
      );

      let emailAddress;

      if (existingEmail) {
        console.log('[FRONTEND] Email already exists, using existing:', existingEmail.id);
        emailAddress = existingEmail;
      } else {
        console.log('[FRONTEND] Creating new email address...');
        // Create email address via Clerk frontend SDK
        emailAddress = await user.createEmailAddress({
          email: email,
        });
        console.log('[FRONTEND] Email address created:', emailAddress.id);
      }

      console.log('[FRONTEND] Preparing verification...');

      // Trigger verification code email
      await emailAddress.prepareVerification({ strategy: 'email_code' });

      console.log('[FRONTEND] Verification code sent successfully');

      // Navigate to verify page with email in state
      navigate("/verification/school-email/verify", {
        state: {
          email,
          emailAddressId: emailAddress.id
        }
      });
    } catch (error: any) {
      console.error('[FRONTEND] Error:', error);
      toast({
        title: "Error",
        description: error?.errors?.[0]?.message || error?.message || "Failed to send code. Check email format.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-bumble-yellow/20 to-background flex items-center justify-center p-4"
      role="main"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-bumble-dark">Verify School Email</CardTitle>
          <CardDescription>
            We'll send a 6-digit verification code to your school email
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Enter your school email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@school.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-describedby="email-hint"
              />
              <p id="email-hint" className="text-xs text-muted-foreground">
                Use your official school or district email
              </p>
            </div>
            <Button
              type="submit"
              className="w-full bg-bumble-yellow text-bumble-dark hover:bg-bumble-yellow/90"
              disabled={loading || !email}
            >
              {loading ? "Sending..." : "Send Code"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
